import { Node, Edge } from 'reactflow';

export enum ComponentType {
  WEB_SERVER = 'WebServer',
  API_GATEWAY = 'ApiGateway',
  MICROSERVICE = 'Microservice',
  DATABASE = 'RDBMS',
  CACHE = 'Cache',
  MESSAGE_BROKER = 'MessageBroker',
  LOAD_BALANCER = 'LoadBalancer',
  FUNCTION = 'ServerlessFunction',
  WORKER = 'Worker',
  SEARCH_ENGINE = 'SearchEngine',
  STORAGE = 'ObjectStorage',
  CDN = 'CDN',
  FIREWALL = 'Firewall',
  KUBERNETES = 'Kubernetes',
  AUTH_SERVICE = 'AuthService',
  NOTIFICATION = 'NotificationService',
  GROUP = 'Group' // New Component
}

export enum ConnectionType {
  HTTP = 'HTTP',
  HTTPS = 'HTTPS',
  TCP = 'TCP',
  UDP = 'UDP',
  GRPC = 'gRPC',
  JDBC = 'JDBC',
  AMQP = 'AMQP',
  WEBSOCKET = 'WebSocket',
  INTERNAL = 'Internal'
}

export interface CustomPort {
  id: string;
  side: 'top' | 'right' | 'bottom' | 'left';
  percentage: number; // 0 to 100
  handleType?: 'source' | 'target';
}

export interface NodeData {
  label: string;
  type: ComponentType;
  locked?: boolean;
  isCluster?: boolean;
  instanceCount?: number; // Number of instances in cluster
  layerId?: string | null; // Layer assignment
  comment?: string; // Comment/annotation
  specs?: {
    cpu?: string;
    memory?: string;
    engine?: string;
    [key: string]: any;
  };
  customPorts?: CustomPort[];
  nestedArchitecture?: {
    nodes: ArchitectureNode[];
    edges: ArchitectureEdge[];
    viewport: { x: number; y: number; zoom: number };
  }; // Internal architecture for nested components
}

export interface EdgeData {
  type: ConnectionType;
  port?: string;
  comment?: string; // Comment/annotation
  customLabel?: string; // Custom text label (separate from connection type)
  labelVisible?: boolean; // Whether to show the label
  labelPosition?: 'auto' | 'top' | 'bottom' | 'left' | 'right'; // Label position
  // Styling properties
  customColor?: string; // Custom color override
  strokeWidth?: 'thin' | 'normal' | 'thick'; // Line thickness
  lineStyle?: 'solid' | 'dashed' | 'dotted'; // Line style
  opacity?: number; // Opacity (0-1)
  // Metadata properties
  latency?: string; // Connection latency (e.g., "50ms", "100ms")
  bandwidth?: string; // Connection bandwidth (e.g., "1Gbps", "100Mbps")
  tags?: string[]; // Tags for categorizing connections
  status?: 'active' | 'inactive' | 'degraded'; // Connection status
}

export type ArchitectureNode = Node<NodeData>;
export type ArchitectureEdge = Edge<EdgeData>;

export interface YamlOutput {
  version: string;
  name: string;
  components: Record<string, {
    type: string;
    specs: any;
    connections: Array<{
      target: string;
      type: string;
      port?: string;
    }>;
  }>;
}

export interface Project {
  id: string;
  name: string;
  created: string; // ISO date
  modified: string;
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  viewport: { x: number; y: number; zoom: number };
  thumbnail?: string; // base64 canvas snapshot
  textElements?: TextElement[]; // Text annotations on canvas
}

export interface ArchitectureTemplate {
  id: string;
  name: string;
  description: string;
  category: 'web' | 'microservices' | 'serverless' | 'data' | 'kubernetes';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  thumbnail: string; // SVG or base64
}

export interface GridSettings {
  enabled: boolean;
  size: number; // Grid size in pixels (10, 20, 50)
  snapToGrid: boolean;
}

export interface TextElement {
  id: string;
  x: number; // Canvas position (not React Flow position)
  y: number;
  text: string;
  style: {
    fontSize: number; // Default: 16
    fontFamily: string; // Default: 'Arial'
    fontWeight: 'normal' | 'bold'; // Default: 'normal'
    fontStyle: 'normal' | 'italic'; // Default: 'normal'
    textDecoration: 'none' | 'underline'; // Default: 'none'
    color: string; // Default: '#000000'
    textAlign: 'left' | 'center' | 'right'; // Default: 'left'
  };
  width?: number; // Optional fixed width for textarea
  height?: number; // Optional fixed height for textarea
}